import React, { useState } from 'react'
import { Card, Button, Input, Select, Table, Td, StatusBadge, Notification } from './ui'
import { AGENTS, AM_LIST } from '../lib/constants'
import { CANONICAL_STAGES, stageOptions, canTransition, requiresReason } from '../lib/pipelineRules'
import { exportPipeline } from '../lib/export'
import { Trash2, Pencil } from 'lucide-react'

const empty = {
  agent: 'Halimah', prospect_name: '', company: '', solution: '',
  est_value: '', bant_score: '3/4', stage: 'Qualified',
  am_assigned: '', meeting_date: '', brief: '',
}

export default function Pipeline({ pipeline, addDeal, updateDealStage, updateDeal, deleteDeal, canEdit, addActivity }) {
  const [form, setForm] = useState(empty)
  const [saving, setSaving] = useState(false)
  const [notif, setNotif] = useState('')
  const [filterStage, setFilterStage] = useState('')
  const [editDeal, setEditDeal] = useState(null)

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const handleSubmit = async () => {
    if (!form.prospect_name || !form.company) {
      setNotif('Prospect name and company are required.')
      return
    }
    setSaving(true)
    try {
      await addDeal(form)
      setNotif('Deal added to pipeline!')
      setForm(empty)
    } catch (e) {
      setNotif('Error: ' + e.message)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this deal from the pipeline?')) return
    await deleteDeal(id)
    setNotif('Deal removed.')
  }

  // Validate the transition, require a reason where needed, persist, and log a
  // stage_change activity (immutable history).
  const handleStageChange = async (deal, toStage) => {
    const from = deal.stage
    if (!canTransition(from, toStage)) {
      setNotif(`Transisi tidak valid: "${from}" → "${toStage}". Lihat aturan pipeline.`)
      return
    }
    let reason = null
    if (requiresReason(from, toStage)) {
      reason = window.prompt(`Alasan memindahkan ke "${toStage}"? (wajib)`)
      if (!reason || !reason.trim()) { setNotif('Dibatalkan — alasan wajib diisi.'); return }
    }
    await updateDealStage(deal.id, toStage)
    if (addActivity) {
      await addActivity({
        actor_agent: deal.agent, activity_type: 'stage_change',
        company_name: deal.company, contact_name: deal.prospect_name,
        stage_from: from, stage_to: toStage, reason: reason ? reason.trim() : null,
      })
    }
    setNotif('Stage updated.' + (reason ? ' Alasan tercatat di history.' : ''))
  }

  const filtered = pipeline.filter(r => !filterStage || r.stage === filterStage)
  const activeCount = pipeline.filter(p => p.stage !== 'Closed Lost' && p.stage !== 'Closed - Lost').length
  // Filter options: canonical stages + any legacy stages still present in data.
  const filterStages = [...new Set([...CANONICAL_STAGES, ...pipeline.map(p => p.stage).filter(Boolean)])]

  return (
    <div>
      <Notification message={notif} onClose={() => setNotif('')} />

      {editDeal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '1.25rem', width: '100%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <div style={{ fontSize: 15, fontWeight: 600 }}>Edit Deal</div>
              <button onClick={() => setEditDeal(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }}>×</button>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '.75rem' }}>
              <Select label="Agent" value={editDeal.agent} onChange={e => setEditDeal(d => ({ ...d, agent: e.target.value }))}>
                {AGENTS.map(a => <option key={a}>{a}</option>)}
              </Select>
              <Input label="Prospect Name" value={editDeal.prospect_name} onChange={e => setEditDeal(d => ({ ...d, prospect_name: e.target.value }))} />
              <Input label="Company" value={editDeal.company} onChange={e => setEditDeal(d => ({ ...d, company: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '.75rem' }}>
              <Input label="Solution" value={editDeal.solution} onChange={e => setEditDeal(d => ({ ...d, solution: e.target.value }))} />
              <Input label="Est. Deal Value" value={editDeal.est_value} onChange={e => setEditDeal(d => ({ ...d, est_value: e.target.value }))} />
              <Select label="BANT Score" value={editDeal.bant_score} onChange={e => setEditDeal(d => ({ ...d, bant_score: e.target.value }))}>
                <option>3/4</option><option>4/4</option>
              </Select>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '.75rem' }}>
              <Select label="Stage" value={editDeal.stage} onChange={e => setEditDeal(d => ({ ...d, stage: e.target.value }))}>
                {stageOptions(editDeal.stage).map(s => <option key={s}>{s}</option>)}
              </Select>
              <Select label="AM Assigned" value={editDeal.am_assigned} onChange={e => setEditDeal(d => ({ ...d, am_assigned: e.target.value }))}>
                <option value="">— Not assigned —</option>
                {AM_LIST.map(a => <option key={a}>{a}</option>)}
              </Select>
              <Input label="Meeting Date" type="date" value={editDeal.meeting_date || ''} onChange={e => setEditDeal(d => ({ ...d, meeting_date: e.target.value }))} />
            </div>
            <div style={{ marginBottom: '.75rem' }}>
              <Input label="Brief" value={editDeal.brief || ''} onChange={e => setEditDeal(d => ({ ...d, brief: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button onClick={() => setEditDeal(null)}>Cancel</Button>
              <Button variant="primary" disabled={saving} onClick={async () => {
                setSaving(true)
                try {
                  const { id, ...updates } = editDeal
                  await updateDeal(id, updates)
                  setNotif('Deal updated!')
                  setEditDeal(null)
                } catch(e) { setNotif('Error: ' + e.message) }
                finally { setSaving(false) }
              }}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Card>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: '0.85rem' }}>Add Deal to Pipeline</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '0.85rem' }}>
          <Select label="Agent" value={form.agent} onChange={e => set('agent', e.target.value)}>
            {AGENTS.map(a => <option key={a}>{a}</option>)}
          </Select>
          <Input label="Prospect Name" value={form.prospect_name} placeholder="Contact name" onChange={e => set('prospect_name', e.target.value)} />
          <Input label="Company" value={form.company} placeholder="PT ..." onChange={e => set('company', e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '0.85rem' }}>
          <Input label="Solution" value={form.solution} placeholder="Cloud, ERP, Cybersecurity..." onChange={e => set('solution', e.target.value)} />
          <Input label="Est. Deal Value" value={form.est_value} placeholder="Rp 500jt" onChange={e => set('est_value', e.target.value)} />
          <Select label="BANT Score" value={form.bant_score} onChange={e => set('bant_score', e.target.value)}>
            <option>3/4</option><option>4/4</option>
          </Select>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '0.85rem' }}>
          <Select label="Stage" value={form.stage} onChange={e => set('stage', e.target.value)}>
            {CANONICAL_STAGES.map(s => <option key={s}>{s}</option>)}
          </Select>
          <Select label="AM Assigned" value={form.am_assigned} onChange={e => set('am_assigned', e.target.value)}>
            <option value="">— Not assigned —</option>
            {AM_LIST.map(a => <option key={a}>{a}</option>)}
          </Select>
          <Input label="Meeting Date" type="date" value={form.meeting_date} onChange={e => set('meeting_date', e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '0.85rem' }}>
          <Input label="Brief" value={form.brief} placeholder="Summary of prospect's needs..." onChange={e => set('brief', e.target.value)} style={{ flex: 3 }} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button size="sm" variant="success" onClick={() => exportPipeline(pipeline)}>Export</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : '+ Add Deal'}
          </Button>
        </div>
      </Card>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Active Pipeline ({activeCount} deals)
          </div>
          <select value={filterStage} onChange={e => setFilterStage(e.target.value)}
            style={{ fontSize: 11, padding: '4px 7px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 6, background: '#fff' }}>
            <option value="">All stages</option>
            {filterStages.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <Table headers={['Agent', 'Prospect', 'Company', 'Solution', 'Est. Value', 'Stage', 'AM', 'Meeting', ...(canEdit ? [''] : [])]}>
          {filtered.length === 0 ? (
            <tr><td colSpan={canEdit ? 9 : 8} style={{ textAlign: 'center', color: '#aaa', padding: 20, fontSize: 13 }}>No deals yet. Add your first deal above!</td></tr>
          ) : filtered.map(r => (
            <tr key={r.id}>
              <Td>{r.agent}</Td>
              <Td style={{ fontWeight: 600 }}>{r.prospect_name}</Td>
              <Td>{r.company}</Td>
              <Td style={{ fontSize: 11 }}>{r.solution}</Td>
              <Td style={{ fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}>{r.est_value}</Td>
              <Td><StatusBadge status={r.stage} /></Td>
              <Td style={{ fontSize: 11 }}>{r.am_assigned || '–'}</Td>
              <Td style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{r.meeting_date || '–'}</Td>
              {canEdit && (
                <Td style={{ whiteSpace: 'nowrap' }}>
                  <button onClick={() => setEditDeal({ ...r })}
                    style={{ background: '#F0F0F0', border: 'none', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', color: '#444', display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, marginRight: 4 }}>
                    <Pencil size={11} /> Edit
                  </button>
                  <select value={r.stage} onChange={e => handleStageChange(r, e.target.value)}
                    style={{ fontSize: 11, padding: '2px 5px', border: '0.5px solid rgba(0,0,0,0.15)', borderRadius: 6, background: '#fff', marginRight: 4 }}>
                    {stageOptions(r.stage).map(s => <option key={s}>{s}</option>)}
                  </select>
                  <button onClick={() => handleDelete(r.id)}
                    style={{ background: 'none', border: '0.5px solid rgba(0,0,0,0.15)', borderRadius: 6, padding: '2px 6px', cursor: 'pointer', color: '#999' }}>
                    <Trash2 size={13} />
                  </button>
                </Td>
              )}
            </tr>
          ))}
        </Table>
      </Card>
    </div>
  )
}
